import { Crown, Shield, X } from "lucide-react";
import type { ChannelMember } from "@/api/connectApi";
import { cn } from "@/lib/utils";
import { presenceDotClass, presenceLabel } from "./chat/chatUi";
import { StatusNoteText, noteText, usePeerStatus } from "./presence/PeerStatus";

/**
 * A channel's people, owners first. Each card shows presence (as the person
 * chose to be seen — "Appear offline" reads as Offline) and their status
 * message, from the shared presence snapshot plus live hub updates.
 */
export function MembersPanel({
    members,
    onClose
}: {
    members: ChannelMember[];
    onClose: () => void;
}) {
    const { presenceOf, noteOf } = usePeerStatus();
    const sorted = [...members].sort((a, b) => {
        // Owners first, then moderators, then members; alphabetical inside.
        const rank = (r: string) => (r === "Owner" ? 0 : r === "Moderator" ? 1 : 2);
        const byRole = rank(a.role) - rank(b.role);
        if (byRole !== 0) return byRole;
        return (a.displayName ?? a.userEmail).localeCompare(b.displayName ?? b.userEmail);
    });

    return (
        <aside
            className="fixed inset-0 z-40 flex w-full flex-col border-l bg-background md:static md:z-auto md:w-72 md:shrink-0"
            aria-label="Channel members"
        >
            <header className="flex h-12 shrink-0 items-center justify-between border-b px-4">
                <h2 className="text-sm font-semibold">Members ({members.length})</h2>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close members"
                    className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent"
                >
                    <X className="h-4 w-4" />
                </button>
            </header>
            <ul className="min-h-0 flex-1 overflow-auto">
                {sorted.map((m) => {
                    const status = presenceOf(m.userId);
                    const note = noteOf(m.userId);
                    const name = m.displayName ?? m.userEmail;
                    return (
                        <li
                            key={m.id}
                            className="flex items-center gap-2 px-3 py-2 hover:bg-accent/40"
                            aria-label={`${name}, ${presenceLabel(status)}${note ? `, ${noteText(note)}` : ""}`}
                        >
                            <div className="relative">
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                                    {name.slice(0, 2).toUpperCase()}
                                </div>
                                <span
                                    className={cn(
                                        "absolute -bottom-0 -right-0 h-2.5 w-2.5 rounded-full ring-2 ring-background",
                                        presenceDotClass(status)
                                    )}
                                    title={presenceLabel(status)}
                                    aria-hidden="true"
                                />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1 truncate text-sm font-medium">
                                    {name}
                                    {m.role === "Owner" ? (
                                        <Crown className="h-3 w-3 text-amber-500" aria-label="Owner" />
                                    ) : m.role === "Moderator" ? (
                                        <Shield className="h-3 w-3 text-primary" aria-label="Moderator" />
                                    ) : null}
                                </div>
                                {note ? (
                                    <StatusNoteText note={note} className="block text-[11px] text-foreground/80" />
                                ) : null}
                                <div className="truncate text-[10px] text-muted-foreground">
                                    {m.userEmail}
                                </div>
                            </div>
                        </li>
                    );
                })}
            </ul>
        </aside>
    );
}
